// Public Privacy Policy — served unauthenticated at /privacy.
// This URL is registered with Meta (App Review) as the app's Privacy Policy
// URL, and the #data-deletion section is the Data Deletion Instructions URL.
// Keep it ACCURATE to what the product actually does — reviewers read it
// against the requested permissions (ads_read, ads_management,
// business_management, read_insights).
export default function Privacy() {
  return (
    <div className="min-h-screen bg-white px-6 py-12 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: July 3, 2026</p>

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">1. Introduction</h2>
          <p>
            AdMoji AdManager ("AdMoji", "we", "our", "us"), available at adsmanager.admoji.com, is an
            AI-powered advertising creative and intelligence platform. This Privacy Policy explains what
            information we collect, how we use it, who we share it with, and the choices you have. By using
            the Service you agree to the practices described here and in our{" "}
            <a href="/terms" className="text-indigo-600 underline">Terms of Service</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">2. Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Account information.</strong> Email address, name, and a hashed password — or, if you
              sign in with Google, your Google account identifier and the name/email Google shares with us.
            </li>
            <li>
              <strong>Brand and product information you provide.</strong> Brand names, websites, logos,
              colors, fonts, product details, images you upload, and creative preferences.
            </li>
            <li>
              <strong>Meta (Facebook) advertising data you authorize.</strong> If you connect a Meta account
              via OAuth, we access and store data from the ad accounts you select: ad account details,
              campaigns, ad sets, ads and their creatives, and performance insights (e.g. spend,
              impressions, clicks, conversions, ROAS, and geographic/hourly breakdowns). We request the
              ads_read, ads_management, business_management, and read_insights permissions for this purpose.
            </li>
            <li>
              <strong>Publicly available ad data.</strong> Ads and brand information retrieved from public
              sources such as Meta's Ad Library, used for competitive research and creative inspiration.
            </li>
            <li>
              <strong>CRM data you connect.</strong> If you connect a supported CRM, we sync customer and
              order records you authorize so the Service can report on revenue and performance.
            </li>
            <li>
              <strong>Payment information.</strong> Payments are processed by Stripe. We store your plan,
              credit balance, and transaction history — we never see or store full card numbers.
            </li>
            <li>
              <strong>Usage data.</strong> Actions you take in the app, generation requests, credit
              consumption, and technical logs (such as session cookies and IP-level request data) needed to
              operate and secure the Service.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">3. How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide, operate, and secure the Service (authentication, sessions, billing, support).</li>
            <li>
              Generate ad creatives, strategy, copy, and analytics for you using AI models, informed by
              your brand data, your connected ad performance data, and public ad research.
            </li>
            <li>
              Build your private brand memory (e.g. brand profiles, creative history, performance digests)
              so the Service improves for your account over time.
            </li>
            <li>
              <strong>Train and improve our AI systems.</strong> We collect and may use data obtained
              through the Service — including data from connected Meta accounts (such as campaign
              structures, ad creatives, and performance metrics), uploaded assets, and product usage — to
              train, fine-tune, and improve our AI models and creative-intelligence systems. Where
              practical, such data is aggregated or de-identified. This is also disclosed in our Terms of
              Service.
            </li>
            <li>Communicate with you about the Service (transactional emails, support responses).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">4. How We Share Information</h2>
          <p>We do not sell your personal information. We share data only with service providers that help us operate:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>AI model providers (e.g. Anthropic via OpenRouter, OpenAI, Google image models via WaveSpeed) that process prompts and content to generate outputs.</li>
            <li>Stripe (payments), Bunny.net (content delivery), and our database/hosting providers (e.g. Neon, DigitalOcean).</li>
            <li>Meta Platforms, when you use the Meta integration (API calls are made on your behalf with your authorization).</li>
            <li>Web-data providers (e.g. Apify, Firecrawl, SearchAPI) used to retrieve publicly available brand and ad information.</li>
            <li>Authorities, if required by law, or parties involved in a merger or acquisition of our business (with notice where required).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">5. Meta Platform Data</h2>
          <p>
            Our use of data received from Meta's APIs complies with the{" "}
            <a href="https://developers.facebook.com/terms/" className="text-indigo-600 underline" target="_blank" rel="noreferrer">
              Meta Platform Terms
            </a>{" "}
            and Developer Policies. We access Meta advertising data only after you explicitly connect your
            account via Meta's OAuth flow, and only for the ad accounts you authorize. You can revoke access
            at any time from the Service (Settings → Connections → Disconnect) or from your Facebook
            settings ("Business Integrations").
          </p>
        </section>

        <section id="data-deletion">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">6. Data Retention & Deletion</h2>
          <p>We retain your data while your account is active. You can delete data at any time:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>
              <strong>Meta data deletion:</strong> disconnecting your Meta account in Settings → Connections
              permanently deletes all synced Meta data from our systems — connections, ad accounts,
              campaigns, ad sets, ads, insights, and derived analyses.
            </li>
            <li>Brands, products, projects, generated creatives, and uploads can be deleted in the app.</li>
            <li>
              For full account deletion, email{" "}
              <a href="mailto:support@admoji.com" className="text-indigo-600 underline">support@admoji.com</a>{" "}
              from your account email and we will delete your account and associated personal data within 30
              days, except records we must keep for legal or billing purposes.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">7. Security</h2>
          <p>
            All traffic is encrypted in transit (HTTPS). Passwords are stored hashed, sessions are
            server-side, and access to production systems is restricted. No method of storage or
            transmission is 100% secure, but we work to protect your information with industry-standard
            measures.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">8. Your Rights</h2>
          <p>
            Depending on your location, you may have rights to access, correct, export, or delete your
            personal data, and to object to or restrict certain processing. Contact us at{" "}
            <a href="mailto:support@admoji.com" className="text-indigo-600 underline">support@admoji.com</a>{" "}
            to exercise these rights.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">9. Children</h2>
          <p>The Service is intended for business use by adults and is not directed to anyone under 18.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be reflected on this
            page with an updated date above.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">11. Contact</h2>
          <p>
            Questions about this policy or your data:{" "}
            <a href="mailto:support@admoji.com" className="text-indigo-600 underline">support@admoji.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}
